/* ============================================================
   Long Run Co. — Rewards section renderer
   Data sources:
   • Subscription bridge (window.LRCMP_LOYALTY, published by portal):
       points balance + redeem catalog/cost (fallback).
   • Loyalty proxy (data-loyalty-base, default /apps/loyalty/cp/api/):
       - customer-loyalty → rewards[] (redeemed shelf, UNUSED only) +
         rewardedFor* booleans (earned state) + availablePoints.
       - <catalog path> (variant-aware redeem rules) → tiles with
         variantId / customerFacingIconUrl / redeemPoints (preferred
         over the bridge catalog when reachable).
   Images resolve from customerFacingIconUrl / productData / variant
   product handle; configurable image-map is the fallback. Actions inert.
   ============================================================ */
(function () {
  "use strict";
  var roots = document.querySelectorAll("[data-lrc-mp-rewards]");
  if (!roots.length) return;

  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }
  function num(v) { var n = parseFloat(v); return isNaN(n) ? null : n; }
  function pick(o, paths, d) {
    for (var i = 0; i < paths.length; i++) { var c = o, ok = true, p = paths[i].split(".");
      for (var j = 0; j < p.length; j++) { if (c != null && typeof c === "object" && p[j] in c) c = c[p[j]]; else { ok = false; break; } }
      if (ok && c != null && c !== "") return c; } return d;
  }
  function toArray(x) { if (Array.isArray(x)) return x; if (x == null) return []; if (typeof x === "object") { var k = ["content","data","items","results","list","rewards","redeemRules","pointRedeemRules"]; for (var i = 0; i < k.length; i++) if (Array.isArray(x[k[i]])) return x[k[i]]; return [x]; } return []; }
  function parseIntLoose(v) { if (v == null) return null; if (typeof v === "number") return isNaN(v) ? null : Math.round(v); var d = String(v).replace(/[^\d]/g, ""); return d ? parseInt(d, 10) : null; }
  function pointsOf(cust) { return pick(cust || {}, ["availablePointsBalance","availablePoints","pointsBalance","points","balance","currentPoints","data.availablePointsBalance","customer.availablePointsBalance"], null); }
  function costOf(o) { var c = parseIntLoose(pick(o, ["redeemPoints","pointsCostText","pointsCost","points","cost","pointValue","requiredPoints","pointsRequired","threshold","amount"], null)); return (c != null) ? c : parseIntLoose(pick(o, ["description"], null)); }
  /* live redeem cost lives ONLY in pointsCostText (e.g. "2,000 pts"); strip non-digits, parseInt; NaN or <= 0 ⇒ null (never redeem) */
  function redeemCost(o) { var raw = pick(o, ["pointsCostText"], null); if (raw == null) return null; var n = parseInt(String(raw).replace(/[^\d]/g, ""), 10); return (isNaN(n) || n <= 0) ? null : n; }
  function nameOf(o, d) { return pick(o, ["customerFacingLabel","title","name","rewardName","label","redeemLabel","description","displayName"], d || "Reward"); }

  /* product image resolution from a reward (icon → productData → variant handle) */
  function productInfo(o) { var pd = pick(o, ["productData","product_data"], null); if (typeof pd === "string") { try { pd = JSON.parse(pd); } catch (e) { pd = null; } } return (pd && typeof pd === "object") ? pd : null; }
  function imageFromReward(o) {
    var icon = pick(o, ["customerFacingIconUrl","imageUrl","image","icon"], null);
    if (icon) return { url: icon };
    var pd = productInfo(o);
    if (pd) {
      var pimg = pick(pd, ["image","featuredImage","featured_image","imageUrl","src"], null) || (Array.isArray(pd.images) ? pd.images[0] : null);
      if (pimg) return { url: (typeof pimg === "object") ? (pimg.src || pimg.url) : pimg };
      var h = pick(pd, ["handle","productHandle"], null); if (h) return { handle: h };
    }
    var h2 = pick(o, ["productHandle","handle","product.handle","variant.product.handle"], null);
    return h2 ? { handle: h2 } : null;
  }

  var FLAG_FIELD = { sms: "rewardedForSms", instagram: "rewardedForInstagram", facebook: "rewardedForFacebook", twitter: "rewardedForTwitter", youtube: "rewardedForYoutube", tiktok: "rewardedForTiktok", newsletter: "rewardedForNewsLetter" };

  /* MEMOISED customer-loyalty fetch — shared with the Refer section via window
     cache (keyed by base) so customer-loyalty is requested ONCE per page. */
  function clFetch(base) {
    var W = window; W.__LRCMP_CL__ = W.__LRCMP_CL__ || {};
    if (W.__LRCMP_CL__[base]) return W.__LRCMP_CL__[base];
    var p = fetch(base + "customer-loyalty", { method: "GET", credentials: "same-origin", headers: { "Accept": "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    W.__LRCMP_CL__[base] = p;
    return p;
  }

  /* design-mode samples */
  function sampleLoyalty() { return { state: "ready", customer: { availablePointsBalance: 3277 }, redeem: [
    { customerFacingLabel: "Free 12oz bag", redeemPoints: 2000 }, { customerFacingLabel: "Travel tumbler", redeemPoints: 3500 }, { customerFacingLabel: "Members hoodie", redeemPoints: 5000 } ] }; }
  function sampleCustomer() { return { availablePoints: 3277, rewardedForSms: true, rewardedForInstagram: true,
    rewards: [ { description: "Get any 1lb bag for free", discountCode: "LRC_W79H10", status: "UNUSED", expireDate: null }, { description: "BOCO run hat", discountCode: "LRC_V6Zcq4", status: "UNUSED", expireDate: null } ] }; }

  Array.prototype.forEach.call(roots, function (root) {
    var DESIGN = root.getAttribute("data-design-mode") === "true" || (typeof window.Shopify !== "undefined" && window.Shopify && window.Shopify.designMode === true);
    var LOYALTY_BASE = (root.getAttribute("data-loyalty-base") || "/apps/loyalty/cp/api/").replace(/\/+$/, "") + "/";
    var CATALOG_PATH = (root.getAttribute("data-catalog-path") || "").trim();
    /* redemption copy — stamped by the section as data-rw-*; safe defaults if absent so the asset stays setting-free */
    var RW_CONFIRM = root.getAttribute("data-rw-confirm") || "Redeem {points} points for {name}?";
    var RW_WORKING = root.getAttribute("data-rw-working") || "Working...";
    var RW_INSUFFICIENT = root.getAttribute("data-rw-insufficient") || "Not enough points yet";
    var RW_SUCCESS = root.getAttribute("data-rw-success") || "Redeemed! Your reward is on the way — refreshing...";
    var RW_ERROR = root.getAttribute("data-rw-error") || "Could not redeem right now. Your points are untouched — please try again.";

    function region(name) { return root.querySelector('[data-mp-region="' + name + '"]'); }
    function has(name) { return !!region(name); }
    function setText(name, v) { var el = region(name); if (el) el.textContent = (v == null || v === "") ? "—" : v; }
    function setHTML(name, h) { var el = region(name); if (el) el.innerHTML = h; }
    function show(el) { if (el) el.classList.remove("lrc-mp__hide"); }
    function hide(el) { if (el) el.classList.add("lrc-mp__hide"); }

    var state = { balance: null, catalogFromLoyalty: false, customer: null, customerId: null };

    /* shared SMS earn-row toggler — callable from the modal success branch AND the load-time status check.
       done=true → green Done badge; done=false → restore the "+points" claimable pill. Recomputes the summary pill. */
    function setSmsRow(done) {
      var row = root.querySelector('.erow[data-flag-key="sms"]'); if (!row) return;
      var ep = row.querySelector(".ep");
      if (done) {
        if (row.classList.contains("done")) return;
        row.classList.add("done");
        if (ep) ep.innerHTML = '<span class="ep-done"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>Done</span>';
      } else {
        if (!row.classList.contains("done")) return;
        row.classList.remove("done");
        if (ep) ep.textContent = "+" + (row.getAttribute("data-earn-points") || "");
      }
      try { recomputeEarnSumShared(); } catch (e) {}
    }
    /* top-level earn-sum recompute (mirrors the nested recomputeEarnSum, reachable from any scope) */
    function recomputeEarnSumShared() {
      var sum = 0;
      Array.prototype.forEach.call(root.querySelectorAll(".erow[data-flag-key]"), function (r) {
        if (r.classList.contains("done")) return;
        var p = parseInt(r.getAttribute("data-earn-points") || "0", 10);
        if (!isNaN(p)) sum += p;
      });
      var pill = root.querySelector("[data-earn-sum]");
      if (pill) { if (sum > 0) { pill.textContent = sum.toLocaleString() + " pts to claim"; pill.hidden = false; } else { pill.hidden = true; } }
    }

    /* ---- redemption: click → confirm → POST (Appstle storefront API, session-cookie) ---- */
    function resolveCustomerId() {
      if (state.customerId) return String(state.customerId);                       /* whatever the asset/state already resolved */
      var c2 = pick(state.customer || {}, ["customerId", "id", "shopifyCustomerId"], null); if (c2) return String(c2);
      if (window.__st && window.__st.cid) return String(window.__st.cid);
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.page && meta.page.customerId) return String(meta.page.customerId);
      return "";
    }
    function rwAct(tile) { return tile.querySelector("[data-rw-act]"); }
    /* ONE reusable slide-up sheet, mounted at the portal ROOT (.rw) — never inside a tile, so
       position:fixed resolves to the viewport (no transformed ancestor; the same place the points
       modal already works). Reuses the proven .scrim/.sheet/.xclose pattern. The redeem picker and
       the confirm card mount into its body; only the container changed, not what they build. */
    function rwSheetBody() {
      var scrim = (root.closest(".rw-wrap") || root).querySelector("[data-rw-sheet]");
      if (!scrim) {
        scrim = document.createElement("div");
        scrim.className = "rw scrim rw-modal"; scrim.setAttribute("data-rw-sheet", "");
        scrim.innerHTML = '<div class="sheet" role="dialog" aria-modal="true" aria-label="Redeem your reward">' +
          '<button type="button" class="xclose" data-modal-close aria-label="Close">×</button>' +
          '<div data-rw-sheet-body></div></div>';
        (root.closest(".rw-wrap") || root).appendChild(scrim);
      }
      scrim.classList.add("show");
      try { scrim.querySelector(".sheet").scrollTop = 0; } catch (e) {}
      return scrim.querySelector("[data-rw-sheet-body]");
    }
    function closeRwSheet() { var scope = root.closest(".rw-wrap") || root; var s = scope.querySelector("[data-rw-sheet]"); if (s) s.classList.remove("show"); }
    function mountActions(tile, o) {                                               /* default state: a Redeem button that closes over o */
      var act = rwAct(tile); if (!act) return;
      act.innerHTML = "";
      var btn = document.createElement("button");
      btn.type = "button"; btn.className = "btn btn-primary btn-sm btn-block"; btn.textContent = "Redeem";
      btn.addEventListener("click", function () { startRedeem(tile, o); });
      act.appendChild(btn);
    }
    /* 4b: redesigned confirmation card + merged add. Entry: branch by contract state (D no-contract/prepaid,
       A picked variant already a paid recurring line, C everything else). Confirm → doRedeem (mint, returns
       the code via F1) → reuse shelfDoAdd untouched for the add (it routes applyToExisting vs doAddThenApply). */
      /* ===== every-order (recurring reward) — Pass 4b ===== */
      var RW_EVERY_ORDER_LIVE = true;   /* KILL-SWITCH: set false to instantly disable every-order (toggle hides) without a redeploy */
      function rwVariantSubscribable(vid, handle) {
        /* PRIMARY: the reward product's own Appstle selling plans (from products.js), keyed by handle — collection-independent. */
        if (handle && Object.prototype.hasOwnProperty.call(subByHandle, handle)) return subByHandle[handle] === true;
        /* SECONDARY: the add-module's published variant flags, if the product happens to be in that collection. */
        if (vid != null) {
          var want = String(vid).replace(/[^0-9]/g, "");
          var data = window.LRCMP_ADD_MODULE;
          var pillars = (data && data.pillars) || [];
          for (var p = 0; p < pillars.length; p++) {
            var prods = (pillars[p] && pillars[p].products) || [];
            for (var i = 0; i < prods.length; i++) {
              var vs = (prods[i] && prods[i].variants) || [];
              for (var j = 0; j < vs.length; j++) {
                if (String(vs[j].id).replace(/[^0-9]/g, "") === want) return vs[j].subscribable === true;
              }
            }
          }
        }
        return false;   /* unknown → conservative: no every-order */
      }
    function showConfirm(tile, o, choice) {
      var act = rwSheetBody(); if (!act) return;   /* mount the confirm card into the slide-up sheet, not the narrow tile slot */
      var variantId = choice ? choice.variantId : null;
      var name = nameOf(o), cost = costOf(o);
      var vlabel = "";
      if (choice && choice.title && String(choice.title) !== String(name)) {
        var vt = String(choice.title);
        var lo = vt.toLowerCase(), nlo = String(name).toLowerCase();
        if (lo.indexOf(nlo) === 0) vt = vt.slice(name.length);               /* drop the leading product name */
        vt = vt.replace(/^[\s\-\/·]+/, "").replace(/[\s\-\/·]+$/, "");        /* trim stray separators */
        vt = vt.replace(/\s*\/\s*/g, " · ");                                  /* "Mango / 1 Pack" → "Mango · 1 Pack" */
        vlabel = vt || String(choice.title);                                 /* never blank if there was a title */
      }
      var vimg = choice && choice.image, vurl = vimg ? (typeof vimg === "string" ? vimg : (vimg.src || vimg.url || null)) : null;
      if (!vurl) { var capImg = tile.querySelector(".cap img"); vurl = capImg ? capImg.getAttribute("src") : null; }
      var thumb = vurl ? '<img src="' + esc(vurl) + '" alt="">' : "🎁";
      var heroImg = vurl ? ('<img src="' + esc(vurl) + '" alt="' + esc(name) + '">') : '<span class="ph">🎁</span>';
      var ruleId = pick(o, ["id", "rewardId", "pointRedeemRuleId", "_id"], null);
      var cid = activeContractId();
      var prepaid = !!(window.LRCMP_IS_ACTIVE_PREPAID && window.LRCMP_IS_ACTIVE_PREPAID());

      act.innerHTML = "";
      var card = document.createElement("div"); card.className = "rw-cc";
      card.innerHTML =
        '<div class="eyebrow">One last step</div>' +
        '<div class="rw-cc-title">' + esc(name) + '</div>' +
        (vlabel ? '<div class="rw-cc-var">' + esc(vlabel) + '</div>' : "") +
        '<div class="rw-ne-hero rw-cc-hero">' + heroImg + '<span class="rw-cc-badge" data-cc-badge>FREE · 100% off</span></div>' +
        '<div class="rw-cc-msg" data-cc-msg></div>' +
        '<div class="rw-cc-act" data-cc-act></div>';
      act.appendChild(card);
      var msgEl = card.querySelector("[data-cc-msg]"), badgeEl = card.querySelector("[data-cc-badge]"), slot = card.querySelector("[data-cc-act]");
      slot.innerHTML = '<div class="rw-cc-spin">Checking your subscription…</div>';

      function onConfirm(branch, primary, back) {
        var oldBal = state.balance;
        doRedeem(tile, o, primary, back, variantId, function (body) {   /* onMinted: mint OK, code in hand */
          var code = body && body.discountCode;
          var newBal = (body && body.availablePoints != null) ? Number(body.availablePoints) : oldBal;
          if (branch === "D" || !cid) {
            playAscent(card, oldBal, newBal);   /* no add — sheet stays open so the member can read the code */
            slot.innerHTML = "";
            var d = document.createElement("div"); d.className = "rw-cc-code";
            d.innerHTML = code ? ('Use code <b>' + esc(code) + '</b> at checkout to claim your reward.') : 'Reward redeemed — find your code in your account.';
            slot.appendChild(d); return;
          }
          if (!code) {   /* F1 surfaced no code → fall back to the proven ledger path (no regression) */
            slot.innerHTML = ""; var s = document.createElement("div"); s.className = "rw-success"; s.textContent = RW_SUCCESS; slot.appendChild(s);
            refreshLoyaltyLive(); return;
          }
          /* merged add: reuse shelfDoAdd untouched — it re-detects overlap and routes to
             applyToExisting (already-subscribed) or doAddThenApply (one-time, isOneTimeProduct=true). */
          var r = { variantId: variantId, discountCode: code, pointRedeemRuleId: ruleId, description: name };
          autoCloseOnAdd(card, slot, oldBal, newBal);   /* FIX 2: on add success → contour, hold ~1.1s, close sheet, THEN tick points in full view */
          var recurring = (slot.getAttribute("data-rw-every") === "1");
          shelfDoAdd(r, slot, cid, code, variantId, recurring);
        });
      }
      function renderActions(branch) {
        slot.innerHTML = "";
        slot.removeAttribute("data-rw-every");
        var rwHandle = (choice && choice.handle) || pick(o, ["productHandle", "handle", "product.handle", "variant.product.handle"], null);
        var showToggle = (branch === "C") && RW_EVERY_ORDER_LIVE && rwVariantSubscribable(variantId, rwHandle);
        if (showToggle) {
          var tog = document.createElement("div"); tog.className = "rw-cc-toggle";
          tog.innerHTML =
            '<div class="rw-cc-choice-label">How do you want it?</div>' +
            '<div class="rw-cc-opts">' +
              '<div class="rw-cc-opt is-sel" data-rw-opt="one"><span class="rw-cc-tick"></span><span class="rw-cc-opt-txt"><b>ONE-TIME</b><i>Just your next order</i></span></div>' +
              '<div class="rw-cc-opt" data-rw-opt="every"><span class="rw-cc-tick"></span><span class="rw-cc-opt-txt"><b>EVERY ORDER</b><i>Add to subscription, first is free</i></span></div>' +
            '</div>';
          slot.appendChild(tog);
          var opts = tog.querySelectorAll("[data-rw-opt]");
          Array.prototype.forEach.call(opts, function (op) {
            op.addEventListener("click", function () {
              Array.prototype.forEach.call(opts, function (o2) { o2.classList.remove("is-sel"); });
              op.classList.add("is-sel");
              var every = op.getAttribute("data-rw-opt") === "every";
              slot.setAttribute("data-rw-every", every ? "1" : "0");
              if (primary) primary.textContent = every ? "Add to every order" : "Add free reward";
            });
          });
        }
        var primary = document.createElement("button"); primary.type = "button"; primary.className = "btn btn-primary btn-sm btn-block";
        primary.textContent = branch === "A" ? "Apply to my subscription" : (branch === "D" ? "Get my code" : "Add free reward");
        var back = document.createElement("button"); back.type = "button"; back.className = "btn btn-ghost btn-sm btn-block"; back.textContent = "Keep my points"; back.style.marginTop = "7px";
        back.addEventListener("click", function () { closeRwSheet(); });
        primary.addEventListener("click", function () { onConfirm(branch, primary, back); });
        slot.appendChild(primary); slot.appendChild(back);
      }

      if (!cid || prepaid) {
        msgEl.textContent = prepaid ? "Prepaid plans claim rewards at checkout." : "Redeem now and use your code at checkout.";
        renderActions("D"); return;
      }
      fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get_contract_lines", contractId: cid }) })
        .then(function (r3) { return r3.json().catch(function () { return null; }); })
        .then(function (jl) {
          var lines = (jl && jl.ok && Array.isArray(jl.lines)) ? jl.lines : [];
          var overlap = lines.some(function (ln) { return ln && ln.basePrice != null && String(ln.variantId) === String(variantId); });
          if (overlap) { badgeEl.textContent = "100% off"; msgEl.textContent = "You're already subscribed to this — we'll apply 100% off your next one."; renderActions("A"); }
          else { msgEl.textContent = cost != null ? ("Spend " + cost.toLocaleString() + " points · added free to your next order.") : "Added free to your next order."; renderActions("C"); }
        })
        .catch(function () { msgEl.textContent = "Added free to your next order."; renderActions("C"); });
    }
    /* cosmetic redeem-ascent: contour line draws itself in (in the card) + points number ticks old→new (header). */
    function drawContour(card) {
      if (!card) return;
      var asc = document.createElement("div"); asc.className = "rw-asc";
      asc.innerHTML = '<svg viewBox="0 0 120 34" preserveAspectRatio="none" aria-hidden="true"><path d="M2 30 L26 22 L48 26 L72 10 L96 16 L118 4"/></svg>';
      card.insertBefore(asc, card.firstChild);
      if (!TILT_REDUCE) { requestAnimationFrame(function () { asc.className = "rw-asc rw-asc-in"; }); } else { asc.className = "rw-asc rw-asc-in"; }
    }
    function playAscent(card, oldBal, newBal) { drawContour(card); tickPoints(oldBal, newBal); }
    /* FIX 2: after shelfDoAdd reports success ("Added ✓"), draw the contour, hold ~1.1s, close the sheet,
       THEN tick the header points in full view (the tick was otherwise hidden behind the scrim). shelfDoAdd
       is untouched — we only observe its success state. Error/"Adding…"/"Add to order" never match. */
    function autoCloseOnAdd(card, slot, oldBal, newBal) {
      if (!slot || typeof MutationObserver === "undefined") return;
      var fired = false;
      var obs = new MutationObserver(function () {
        var b = slot.querySelector("button[disabled]");
        if (!fired && b && /Added/.test(b.textContent || "")) {
          fired = true; obs.disconnect();
          drawContour(card);
          setTimeout(function () { closeRwSheet(); tickPoints(oldBal, newBal); }, TILT_REDUCE ? 0 : 1100);
        }
      });
      obs.observe(slot, { childList: true, subtree: true });
      setTimeout(function () { try { obs.disconnect(); } catch (e) {} }, 15000);   /* safety: stop watching after 15s */
    }
    function tickPoints(from, to) {
      if (!has("points")) return;
      from = Number(from); to = Number(to);
      if (!isFinite(to)) return;
      if (!isFinite(from) || TILT_REDUCE || from === to) { setText("points", Math.round(to).toLocaleString()); return; }
      var start = null, dur = 750;
      function step(ts) {
        if (start == null) start = ts;
        var p = Math.min(1, (ts - start) / dur), eased = 1 - Math.pow(1 - p, 3);
        setText("points", Math.round(from + (to - from) * eased).toLocaleString());
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    function redeemErr(tile, o) {                                                  /* restore the Redeem button + inline error above it */
      closeRwSheet();                                                              /* mint error → close the sheet so the tile-level error isn't hidden behind it */
      mountActions(tile, o);
      var act = rwAct(tile); if (!act) return;
      var e = document.createElement("div"); e.className = "rw-err"; e.textContent = RW_ERROR;
      act.insertBefore(e, act.firstChild);
    }
    /* live refresh after a successful redeem — re-fetch the loyalty ledger (busting the
       shared memo cache), repaint balance + shelf + earned flags, then re-render the
       catalog tiles against the new (lower) balance. No page reload. */
    function refreshLoyaltyLive() {
      try { if (window.__LRCMP_CL__) delete window.__LRCMP_CL__[LOYALTY_BASE]; } catch (e) {}
      clFetch(LOYALTY_BASE).then(function (cust) {
        if (cust && typeof cust === "object") {
          state.customer = cust;
          var b = num(pointsOf(cust)); if (b == null) b = num(pick(cust, ["availablePoints"], null));
          if (b != null) { state.balance = b; renderPoints(b); }
          renderShelf(toArray(pick(cust, ["rewards"], [])));
          markEarnedFromFlags(cust);
        }
        loadCatalog();
      });
    }
    /* productData → redeemable variants. Variant-type entries carry the Shopify variant id directly;
       a product-level rule resolves via /products/{handle}.js (cached). Returns [{variantId,title}]. */
    var variantCache = {}, oosByHandle = {}, imgByHandle = {}, posByHandle = {}, prodImgByHandle = {}, varTitleByHandle = {};
    var subByHandle = {};   /* handle → true/false: does this product have Appstle selling plans (subscribable)? from products.js */
    function readSubscribable(p) {   /* a product.js payload is subscribable if any variant has selling_plan_allocations, or any selling_plan_group is appstle */
      if (!p) return false;
      var groups = p.selling_plan_groups;
      if (Array.isArray(groups) && groups.some(function (g) { return g && (g.app_id === "appstle" || (g.selling_plans && g.selling_plans.length)); })) return true;
      var vs = p.variants;
      return Array.isArray(vs) && vs.some(function (v) { return v && v.selling_plan_allocations && v.selling_plan_allocations.length > 0; });
    }
    /* Loyalty redeem rules (admin, via backend get_redeem_rules) cached ONCE by ruleId → parsed productData
       entries. productData self-classifies every reward: VARIANT entries = the pickable flavors; a lone PRODUCT
       entry = single-product (no pick). Replaces the old hardcoded RULE_HANDLE map. */
    function ensureRedeemRules() {
      if (state.redeemRulesP) return state.redeemRulesP;
      var cid = activeContractId();
      if (!cid) return Promise.resolve(null);                                       /* no contract yet → retry on next call, don't memoize */
      state.ruleProductData = state.ruleProductData || {};
      state.redeemRulesP = fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get_redeem_rules", contractId: cid }) })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (j) {
          var rules = (j && j.ok && Array.isArray(j.rules)) ? j.rules : [];
          rules.forEach(function (rule) {
            var pd = rule && rule.productData;
            if (typeof pd === "string") { try { pd = JSON.parse(pd); } catch (e) { pd = null; } }
            if (Array.isArray(pd) && rule.id != null) state.ruleProductData[String(rule.id)] = pd;
          });
          return state.ruleProductData;
        })
        .catch(function () { state.redeemRulesP = null; return null; });            /* on failure allow a retry */
      return state.redeemRulesP;
    }
    function dropOos(list, oos) { return list.filter(function (c) { return !oos[String(c.variantId)]; }); }
    function imgUrl(im) { return im ? (typeof im === "string" ? im : (im.src || im.url || null)) : null; }   /* normalize {src}/string */
    /* per-variant featured_image (from products.js) beats productData's flat product-level imageSrc — but only
       overwrite a choice's image when THAT variant actually has its own featured_image (else keep the flat one). */
    /* attach per-variant featured_image (beats productData's flat imageSrc) AND pos = Shopify native variant order */
    function enrichImages(list, imgs, pos, prodImg) { if (list) list.forEach(function (c) { var k = String(c.variantId); if (imgs) { var u = imgs[k]; if (u) c.image = u; } if (pos && pos[k] != null) c.pos = pos[k]; if (prodImg && !c.prodImage) c.prodImage = prodImg; }); return list; }
    function applyOosFilter(list, handle) {                                         /* drop OOS + attach per-variant images + native order — ONE products.js fetch, cached */
      if (!handle || !list || !list.length) return Promise.resolve(list || []);
      if (oosByHandle[handle]) return Promise.resolve(dropOos(enrichImages(list, imgByHandle[handle], posByHandle[handle], prodImgByHandle[handle]), oosByHandle[handle]));
      return fetch("/products/" + encodeURIComponent(handle) + ".js", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var oos = {}, imgs = {}, pos = {}, titles = {};
          if (p && Array.isArray(p.variants)) p.variants.forEach(function (v, idx) { if (v.available === false) oos[String(v.id)] = 1; var u = imgUrl(v.featured_image); if (u) imgs[String(v.id)] = u; var t = v.public_title || v.title; if (t) titles[String(v.id)] = t; pos[String(v.id)] = idx; });   /* idx = native order; titles = per-variant labels */
          var pImg = p ? (imgUrl(p.featured_image) || (Array.isArray(p.images) ? imgUrl(p.images[0]) : null)) : null;   /* product-level image (string URL) — fallback when a variant has no image */
          oosByHandle[handle] = oos; imgByHandle[handle] = imgs; posByHandle[handle] = pos; prodImgByHandle[handle] = pImg; varTitleByHandle[handle] = titles;
          subByHandle[handle] = readSubscribable(p);
          return dropOos(enrichImages(list, imgs, pos, pImg), oos);
        })
        .catch(function () { return list; });                                       /* uncertain → show all (don't hide a real flavor) */
    }
    /* lazy products.js loader (shelf cold-cache): fills the variant-label map (+ image/pos/prod image) once, cached */
    function ensureVariantData(handle) {
      if (!handle || varTitleByHandle[handle]) return Promise.resolve();
      return fetch("/products/" + encodeURIComponent(handle) + ".js", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var imgs = {}, titles = {}, pos = {};
          if (p && Array.isArray(p.variants)) p.variants.forEach(function (v, idx) { var u = imgUrl(v.featured_image); if (u) imgs[String(v.id)] = u; var t = v.public_title || v.title; if (t) titles[String(v.id)] = t; pos[String(v.id)] = idx; });
          varTitleByHandle[handle] = titles;
          if (!imgByHandle[handle]) imgByHandle[handle] = imgs;
          if (!posByHandle[handle]) posByHandle[handle] = pos;
          if (prodImgByHandle[handle] == null) prodImgByHandle[handle] = p ? (imgUrl(p.featured_image) || (Array.isArray(p.images) ? imgUrl(p.images[0]) : null)) : null;
        })
        .catch(function () { varTitleByHandle[handle] = {}; });   /* mark attempted so we don't refetch in a loop */
    }
    function resolveRedeemVariants(o) {
      var ruleId = String(pick(o, ["id", "rewardId", "pointRedeemRuleId", "redeemRuleId", "pointRedeemRuleRuleId", "_id"], ""));
      return ensureRedeemRules().then(function () {
        /* PRIMARY: cached productData from get_redeem_rules (by ruleId); else the row's own productData. */
        var entries = (state.ruleProductData && state.ruleProductData[ruleId]) || null;
        if (!entries) {
          var pd = pick(o, ["productData", "product_data"], null);
          if (typeof pd === "string") { try { pd = JSON.parse(pd); } catch (e) { pd = null; } }
          entries = Array.isArray(pd) ? pd : (pd && typeof pd === "object" ? [pd] : []);
        }
        /* VARIANT entries ONLY — drops any PRODUCT parent entry (e.g. Tea's) so it never appears as a flavor. */
        var variantEntries = entries.filter(function (e) { return e && (e.type === "VARIANT" || e.variant === true); });
        if (variantEntries.length >= 1) {
          var vHandle = pick(variantEntries[0] || {}, ["productHandle", "handle"], null) || pick(entries[0] || {}, ["productHandle", "handle"], null);
          var list = variantEntries.map(function (e) { return { variantId: String(e.id == null ? "" : e.id).replace(/[^0-9]/g, "") || e.id, title: e.title, image: pick(e, ["image", "featuredImage", "featured_image", "imageUrl", "imageSrc", "src"], null), handle: vHandle }; });
          return applyOosFilter(list, vHandle);
        }
        /* FALLBACK: a product-level handle → /products/{handle}.js (existing behavior, OOS-filtered). */
        var handle = pick(entries[0] || {}, ["productHandle", "handle"], null);
        if (!handle) return [];                                                      /* non-product reward ⇒ no variant */
        if (variantCache[handle]) return variantCache[handle];
        return fetch("/products/" + encodeURIComponent(handle) + ".js", { credentials: "same-origin" })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            var pImg = p ? (imgUrl(p.featured_image) || (Array.isArray(p.images) ? imgUrl(p.images[0]) : null)) : null;   /* product-level image fallback */
            var l = (p && Array.isArray(p.variants)) ? p.variants.filter(function (v) { return v.available !== false; }).map(function (v, idx) { return { variantId: v.id, title: v.public_title || v.title, image: imgUrl(v.featured_image) || imgUrl(v.image) || null, pos: idx, prodImage: pImg, handle: handle }; }) : [];   /* exclude OOS; pos = native order */
            subByHandle[handle] = readSubscribable(p);
            variantCache[handle] = l; return l;
          })
          .catch(function () { variantCache[handle] = []; return []; });
      });
    }
    /* Redeem entry point: resolve variants → 0/1 option goes straight to confirm; 2+ opens an inline
       flavor picker (reuses the confirm chrome) and confirm follows the pick. */
    function startRedeem(tile, o) {
      resolveRedeemVariants(o).then(function (choices) {
        if (choices && choices.length > 1) {
          showPicker(choices, rwSheetBody(),
            function (c) { showConfirm(tile, o, c); },
            function () { closeRwSheet(); });
        } else showConfirm(tile, o, (choices && choices[0]) ? choices[0] : null);
      });
    }
    /* Generalized inline flavor picker: renders into `container`, calls onPick({variantId,title}) on select
       and onCancel() on cancel. (Redeem tiles pass rwAct(tile); the shelf passes its action slot.) */
    /* Flavor-only labels: strip the longest common prefix + suffix shared across ALL choice titles,
       then trim stray separators. e.g. "Caffyne … Pouches - {Flavor} / 1 Pack" → "{Flavor}". */
    function pickerLabels(choices) {
      var titles = choices.map(function (c) { return String(c.title == null ? "" : c.title); });
      if (titles.length < 2) return titles.slice();
      /* 1) strip the longest common PREFIX (the product-name portion), keeping everything after it. */
      var pre = titles[0]; titles.forEach(function (t) { var i = 0; while (i < pre.length && i < t.length && pre.charAt(i) === t.charAt(i)) i++; pre = pre.slice(0, i); });
      var mids = titles.map(function (t) { return t.slice(pre.length).replace(/^[\s\-\/]+/, ""); });
      /* 2) WORD-level common-suffix strip: drop whole whitespace-delimited trailing tokens shared by ALL options
         (e.g. "/ 1 Pack"). A glued unit like "3mg" is a single token → never split or stripped. Always keep >=1. */
      var toks = mids.map(function (m) { return m.split(/\s+/).filter(Boolean); });
      while (toks.every(function (a) { return a.length > 1; })) {
        var last = toks[0][toks[0].length - 1];
        if (!toks.every(function (a) { return a[a.length - 1] === last; })) break;
        toks.forEach(function (a) { a.pop(); });
      }
      return toks.map(function (a, i) {
        var s = a.join(" ").replace(/^[\s\-\/]+/, "").replace(/[\s\-\/]+$/, "");
        return s || mids[i] || titles[i];   /* fall back to full post-prefix text, then the full title */
      });
    }
    function showPicker(choices, container, onPick, onCancel) {
      if (!container) return;
      container.innerHTML = "";
      /* reward-level image (parent row .cap img / .shelf .si img) used only when a variant has no image of its own. */
      var row = container.closest(".ptile, .shelf");
      var rowImg = row && row.querySelector(".cap img, .si img");
      var imgSrc = rowImg ? rowImg.getAttribute("src") : null;
      var head = document.createElement("div"); head.className = "rw-pick-head";
      head.innerHTML = '<div class="eyebrow">Redeem</div><div class="rw-pick-title">Choose your flavor</div>';   /* established eyebrow+beacon convention (matches the page eyebrows) */
      var grid = document.createElement("div"); grid.className = "rw-pick-grid";
      /* Group variants by FIRST option (flavor); the SECOND option becomes a dropdown.
         title = "Product - <Flavor> / <Option>[ / extra]" (primary) OR "<Flavor> / <Option>" (fallback public_title).
         Options never contain " - ", so take everything after the LAST " - ", then split on " / ":
         parts[0] = flavor, parts[1] = dropdown option. No " / " ⇒ one tile per variant (socks, single-dimension). */
      function optStr(t) { t = String(t == null ? "" : t); var d = t.lastIndexOf(" - "); return d >= 0 ? t.slice(d + 3) : t; }
      function imgOf(c) { var v = c && c.image, u = v ? (typeof v === "string" ? v : (v.src || v.url || null)) : null; return u || imgSrc; }
      function groupImg(g) { var i, c; for (i = 0; i < g.options.length; i++) { c = g.options[i] && g.options[i].choice; var u = c && imgUrl(c.image); if (u) return u; } for (i = 0; i < g.options.length; i++) { c = g.options[i] && g.options[i].choice; if (c && c.prodImage) return c.prodImage; } return imgSrc; }   /* variant image → product image → reward image → (glyph) */
      var groups = [], byFlavor = {};
      choices.forEach(function (c) {
        var parts = optStr(c.title).split(" / ");
        var flavor = (parts[0] || c.title || "Option").trim();
        var second = parts.length > 1 ? (parts[1] || "").trim() : null;
        var key = flavor.toLowerCase();
        var g = byFlavor[key];
        if (!g) { g = { flavor: flavor, options: [] }; byFlavor[key] = g; groups.push(g); }
        g.options.push({ label: second, choice: c });   /* choice is the ORIGINAL {variantId,title,image} — the sealed resolution */
      });
      /* Change B: order each flavor's dropdown by Shopify native order (choice.pos from products.js variant order);
         no native order ⇒ stable (no-op). Mirrors how options are arranged on the product — no hardcoded lists. */
      groups.forEach(function (g) { if (g.options.length > 1) g.options.sort(function (a, b) { var pa = a.choice && a.choice.pos, pb = b.choice && b.choice.pos; return (pa == null || pb == null) ? 0 : pa - pb; }); });
      /* resolve ONCE, on Continue: selected flavor + its dropdown's current value → original choice → onPick unchanged */
      function fire(g, sel) { var i = sel ? (sel.selectedIndex < 0 ? 0 : sel.selectedIndex) : 0; var op = g.options[i] || g.options[0]; if (op && op.choice && onPick) onPick(op.choice); }
      /* pinned footer: Continue (amber, disabled until a flavor is selected) + Cancel */
      var foot = document.createElement("div"); foot.className = "rw-pick-foot";
      var cont = document.createElement("button"); cont.type = "button"; cont.className = "btn btn-primary btn-block rw-pick-continue"; cont.textContent = "Continue"; cont.disabled = true;
      var cancel = document.createElement("button"); cancel.type = "button"; cancel.className = "btn btn-ghost btn-sm btn-block"; cancel.textContent = "Cancel"; cancel.style.marginTop = "7px";
      cancel.addEventListener("click", function () { if (onCancel) onCancel(); });
      foot.appendChild(cont); foot.appendChild(cancel);
      /* Change A: tap SELECTS (single-select highlight) — it does NOT advance. Continue advances. */
      var selected = null, tiles = [];
      function selectTile(g, tile, select) {
        selected = { group: g, select: select };
        tiles.forEach(function (t) { t.classList.remove("is-selected"); t.setAttribute("aria-pressed", "false"); });
        tile.classList.add("is-selected"); tile.setAttribute("aria-pressed", "true");
        cont.disabled = false;
      }
      cont.addEventListener("click", function () { if (selected) fire(selected.group, selected.select); });
      groups.forEach(function (g) {
        var tile = document.createElement("div"); tile.className = "rw-pick-tile"; tile.setAttribute("role", "button"); tile.setAttribute("tabindex", "0"); tile.setAttribute("aria-pressed", "false");
        var src = groupImg(g);   /* representative image: first variant in the flavor that has its own image */
        var thumb = src ? '<img src="' + esc(src) + '" alt="">' : "🎁";
        var hasLabels = g.options.some(function (op) { return op.label != null && op.label !== ""; });   /* show the dropdown whenever a real 2nd option exists — even a single one (Decaf → "Fresh Ground"); socks (size-only, no 2nd option) stay plain */
        var selHtml = hasLabels ? ('<select class="rw-pick-opt" aria-label="' + esc(g.flavor) + ' option">' + g.options.map(function (op, oi) { return '<option value="' + oi + '">' + esc(op.label || ("Option " + (oi + 1))) + '</option>'; }).join("") + '</select>') : "";
        tile.innerHTML = '<span class="rw-pick-thumb">' + thumb + '</span>' +
          '<span class="rw-pick-info"><span class="rw-pick-name">' + esc(g.flavor) + '</span>' + selHtml + '</span>' +
          '<span class="rw-pick-free">Free</span>';
        var select = tile.querySelector(".rw-pick-opt");
        if (select) { var stop = function (e) { e.stopPropagation(); }; ["click", "pointerdown", "mousedown", "change", "keydown"].forEach(function (ev) { select.addEventListener(ev, stop); }); }   /* changing the dropdown must NOT select/redeem */
        tile.addEventListener("click", function () { selectTile(g, tile, select); });
        tile.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectTile(g, tile, select); } });
        tiles.push(tile); grid.appendChild(tile);
      });
      container.appendChild(head); container.appendChild(grid); container.appendChild(foot);
      /* staggered entrance: tiles are visible by default; each clears its own inline hide on its
         own timer, so a skipped timer can never permanently hide a tile. Reduced-motion = no stagger. */
      if (!TILT_REDUCE) {
        Array.prototype.forEach.call(grid.children, function (t, i) {
          t.style.opacity = "0"; t.style.transform = "translateY(10px)"; t.style.transition = "opacity .34s ease, transform .34s ease";
          setTimeout(function () { t.style.opacity = ""; t.style.transform = ""; }, 55 + i * 70);
        });
      }
    }
    function doRedeem(tile, o, yes, no, variantId, onMinted) {
      var redeemRuleId = pick(o, ["id", "rewardId", "pointRedeemRuleId", "_id"], null);   /* the rule id from the catalog row */
      if (redeemRuleId == null) { redeemErr(tile, o); return; }
      yes.disabled = true; if (no) no.disabled = true; yes.textContent = RW_WORKING;
      /* Backend mints the reward + discount code AND deducts points via the Loyalty Admin API. It supplies
         the SERVER-verified customerId (we send none); points omitted so Appstle uses the rule's value. */
      fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "redeem_loyalty", redeemRuleId: redeemRuleId, variantId: variantId }) })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (body) {
          if (!body || body.ok !== true) {                                          /* ok:false ⇒ surface reason, no success, points untouched */
            if (window.console) console.warn("[lrc-redeem] not ok:", body && body.reason);
            redeemErr(tile, o); return;
          }
          if (onMinted) { onMinted(body); return; }                                 /* merged confirm flow drives the add; skip the default success UI */
          var act = rwAct(tile); if (act) { act.innerHTML = ""; var s = document.createElement("div"); s.className = "rw-success"; s.textContent = RW_SUCCESS; act.appendChild(s); }
          refreshLoyaltyLive();                                                      /* repaints balance + moves the reward onto the shelf */
        })
        .catch(function (err) { if (window.console) console.warn("[lrc-redeem] failed:", err && err.message); redeemErr(tile, o); });
    }

    function lapi(path) {
      return fetch(LOYALTY_BASE + path.replace(/^\/+/, ""), { method: "GET", credentials: "same-origin", headers: { "Accept": "application/json" } })
        .then(function (r) { if (!r.ok) throw r.status; var ct = r.headers.get("content-type") || ""; return ct.indexOf("json") > -1 ? r.json() : r.text(); })
        .catch(function () { return null; });
    }

    /* ---- image resolution helpers ----
       ONE map, keyed by redeem-rule id, shared by BOTH catalog tiles (reward `id`)
       and the redeemed shelf (`pointRedeemRuleId`) — same id ⇒ identical image, so it
       "sticks" when a reward moves catalog → redeemed. id is primary; exact name is the
       fallback if an id lookup misses (catalog `name` ↔ redeemed `description`). */
    var prodCache = {}, imgMap = {};
    try { imgMap = JSON.parse(root.getAttribute("data-image-map") || "{}"); } catch (e) { imgMap = {}; }
    function mapLookup(idVal, nameVal) {
      if (idVal != null && idVal !== "" && imgMap[String(idVal)]) return imgMap[String(idVal)];
      var k = String(nameVal || "").toLowerCase().trim();
      return (k && imgMap[k]) ? imgMap[k] : null;
    }
    function productImage(handle) {
      if (prodCache[handle] !== undefined) return Promise.resolve(prodCache[handle]);
      return fetch("/products/" + encodeURIComponent(handle) + ".js", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { var img = (p && (p.featured_image || (p.images && p.images[0]))) || null; prodCache[handle] = img; return img; })
        .catch(function () { prodCache[handle] = null; return null; });
    }
    function resolveTileImages() {
      Array.prototype.forEach.call(root.querySelectorAll("[data-rw-handle]"), function (tile) {
        var h = tile.getAttribute("data-rw-handle"); if (!h) return;
        var vvid = tile.getAttribute("data-rw-variant");
        if (vvid) {   /* shelf variant card: fill the variant LABEL (+ variant image) from the SAME products.js */
          ensureVariantData(h).then(function () {
            var t = varTitleByHandle[h] && varTitleByHandle[h][String(vvid)];
            var sd = tile.querySelector(".sd"); if (t && sd && tile.getAttribute("data-rw-meta")) { sd.textContent = t; tile.removeAttribute("data-rw-meta"); }
            var si = tile.querySelector(".si");
            if (si && !si.querySelector("img")) { var vimg = (imgByHandle[h] && imgByHandle[h][String(vvid)]) || prodImgByHandle[h]; if (vimg) si.innerHTML = '<img src="' + esc(vimg) + '" alt="" loading="lazy">'; }
          });
          return;
        }
        productImage(h).then(function (img) {
          if (!img) return;
          var cap = tile.querySelector(".cap");
          if (cap) { cap.className = "cap cap-img"; cap.innerHTML = '<img src="' + esc(img) + '" alt="" loading="lazy">'; return; }
          var si = tile.querySelector(".si"); if (si) si.innerHTML = '<img src="' + esc(img) + '" alt="" loading="lazy">';
        });
      });
    }

    /* ---- points ---- */
    function renderPoints(balance) {
      if (!has("points")) return;
      if (balance == null) { setText("points", "—"); return; }
      var n = Math.round(Number(balance));
      if (!isFinite(n)) { setText("points", "—"); return; }
      if (!state.balanceShown && !TILT_REDUCE) { state.balanceShown = true; tickPoints(0, n); }
      else { state.balanceShown = true; setText("points", n.toLocaleString()); }
    }
    function renderHeroBar(closest) {
      var fill = root.querySelector('[data-mp-region="elev-fill"]');
      if (!fill) return;
      var wrap = root.querySelector('[data-mp-region="elev"]');
      var to = root.querySelector('[data-mp-region="elev-to"]');
      var bal = Number(state.balance) || 0, pct, txt;
      var ok = false;
      if (closest) {
        var cost = redeemCost(closest);
        if (isFinite(cost) && cost > 0 && cost > bal) {
          pct = Math.max(0, Math.min(100, Math.round(bal / cost * 100)));
          txt = "<b>" + (cost - bal).toLocaleString() + "</b> to " + nameOf(closest);
          ok = true;
        }
      }
      if (!ok) {
        pct = 100;
        var ul = (wrap && wrap.getAttribute("data-unlocked-label")) || "Everything unlocked";
        txt = "<b>" + ul + "</b>";
      }
      if (to) to.innerHTML = txt;
      if (wrap && wrap.hasAttribute("hidden")) wrap.removeAttribute("hidden");
      var apply = function () { fill.style.width = pct + "%"; };
      if (TILT_REDUCE || state.heroBarShown) { apply(); }
      else { requestAnimationFrame(function () { setTimeout(apply, 200); }); }
      state.heroBarShown = true;
      bootHeroExtras();
    }
    function bootHeroExtras() {
      if (state.heroExtrasBooted) return;
      state.heroExtrasBooted = true;
      if (TILT_REDUCE) return;
      var topo = root.querySelector('[data-mp-region="topo"]');
      if (!topo) return;
      var ticking = false;
      window.addEventListener("scroll", function () {
        if (ticking) return; ticking = true;
        requestAnimationFrame(function () { topo.style.transform = "translateY(" + (window.scrollY * 0.28) + "px)"; ticking = false; });
      }, { passive: true });
    }

    /* ---- redeemable tiles (source = "loyalty" rules or "bridge" options) ---- */
    var GLYPHS = ["🎁", "☕", "⚡", "🏔"];
    /* cheap press-tilt: rotate the card toward the pointer + slight scale on pointerdown,
       reset on up/leave/cancel. Skipped entirely under prefers-reduced-motion. Pure CSS
       transform (no layout), and it never touches the CTA slot or the redeem wiring. */
    var TILT_REDUCE = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    function wireTilt(tile) {
      if (TILT_REDUCE || tile.__tilt) return; tile.__tilt = 1;
      function reset() { tile.style.transform = ""; }
      tile.addEventListener("pointerdown", function (e) {
        var r = tile.getBoundingClientRect(); if (!r.width || !r.height) return;
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        tile.style.transform = "perspective(520px) rotateX(" + (py * -6).toFixed(2) + "deg) rotateY(" + (px * 6).toFixed(2) + "deg) scale(.975)";
      });
      tile.addEventListener("pointerup", reset);
      tile.addEventListener("pointerleave", reset);
      tile.addEventListener("pointercancel", reset);
    }
      function openNotEnough(o) {
        var body = rwSheetBody(); if (!body) return;
        var cost = redeemCost(o) || 0;
        var bal = Number(state.balance) || 0;
        var togo = Math.max(0, cost - bal);
        var pct = cost > 0 ? Math.max(0, Math.min(100, Math.round(bal / cost * 100))) : 0;
        var name = nameOf(o);
        var ruleId = pick(o, ["id", "rewardId", "pointRedeemRuleId", "_id"], null);
        var mapUrl = mapLookup(ruleId, name);
        var im = mapUrl ? null : imageFromReward(o);
        var url = mapUrl || (im && im.url) || null;
        var hero = url ? ('<img src="' + esc(url) + '" alt="' + esc(name) + '">') : ('<span class="ph">' + GLYPHS[0] + '</span>');
        body.innerHTML =
          '<div class="eyebrow">Almost there</div>' +
          '<h3 class="rw-ne-title">' + esc(name) + '</h3>' +
          '<div class="rw-ne-hero">' + hero + '</div>' +
          '<p class="rw-ne-ptline">' + cost.toLocaleString() + ' points to redeem</p>' +
          '<div class="rw-ne-prog"><div class="rw-ne-track"><i class="rw-ne-fill"></i></div>' +
          '<div class="rw-ne-lbls"><span>' + bal.toLocaleString() + ' pts</span><span class="togo">' + togo.toLocaleString() + ' to go</span></div></div>' +
          '<p class="rw-ne-detail">Keep stacking points on every order — members earn <b>50% faster</b>. You\'re <b>' + pct + '%</b> of the way to this reward.</p>' +
          '<button type="button" class="btn btn-block rw-ne-cta" data-rw-ways>See ways to earn</button>';
        var ways = body.querySelector('[data-rw-ways]');
        if (ways) ways.addEventListener('click', function () { closeRwSheet(); var earn = root.querySelector('.earn'); if (earn && earn.scrollIntoView) earn.scrollIntoView({ behavior: TILT_REDUCE ? 'auto' : 'smooth', block: 'start' }); });
        var fill = body.querySelector('.rw-ne-fill');
        if (fill) { if (TILT_REDUCE) fill.style.width = pct + '%'; else requestAnimationFrame(function () { setTimeout(function () { fill.style.width = pct + '%'; }, 80); }); }
      }
      function setupSmsModal() {
        var scope = root.closest(".rw-wrap") || document;
        var m = scope.querySelector('[data-modal="smsModal"]'); if (!m || m.__smsWired) return; m.__smsWired = 1;
        var submit = m.querySelector("[data-rw-sms-submit]");
        var phoneEl = m.querySelector("[data-rw-sms-phone]");
        var emailEl = m.querySelector("[data-rw-sms-email]");
        var errEl = m.querySelector("[data-rw-sms-err]");
        var closeEls = m.querySelectorAll("[data-modal-close]");
        Array.prototype.forEach.call(closeEls, function (x) { x.addEventListener("click", function () { m.classList.remove("show"); }); });
        m.addEventListener("click", function (e) { if (e.target === m) m.classList.remove("show"); });
        (function smsIosLock() {
          if (m.__iosLock) return; m.__iosLock = 1;
          var sheet = m.querySelector(".sheet");
          var locked = false, savedY = 0;
          function lock() {
            if (locked) return; locked = true;
            savedY = window.pageYOffset || document.documentElement.scrollTop || 0;
            document.body.style.position = "fixed"; document.body.style.top = (-savedY) + "px";
            document.body.style.left = "0"; document.body.style.right = "0"; document.body.style.width = "100%";
          }
          function unlock() {
            if (!locked) return; locked = false;
            document.body.style.position = ""; document.body.style.top = "";
            document.body.style.left = ""; document.body.style.right = ""; document.body.style.width = "";
            window.scrollTo(0, savedY);
          }
          var obs = new MutationObserver(function () {
            if (m.classList.contains("show")) { if (!locked) { lock(); if (sheet) sheet.scrollTop = 0; } }
            else { if (locked) unlock(); }
          });
          obs.observe(m, { attributes: true, attributeFilter: ["class"] });
          if (sheet) {
            sheet.addEventListener("focusin", function (e) {
              var t = e.target;
              if (!t || (t.tagName !== "INPUT" && t.tagName !== "TEXTAREA")) return;
              setTimeout(function () {
                try { var sr = sheet.getBoundingClientRect(), tr = t.getBoundingClientRect(); sheet.scrollTop += (tr.top - sr.top) - 16; } catch (err) {}
              }, 250);
            });
          }
        })();
        if (!submit) return;
        submit.addEventListener("click", function () {
          if (errEl) errEl.hidden = true;
          var raw = (phoneEl && phoneEl.value || "").replace(/[^0-9]/g, "");
          var ok10 = raw.length === 10, ok11 = raw.length === 11 && raw[0] === "1";
          if (!ok10 && !ok11) { if (errEl) { errEl.textContent = "Enter a valid US mobile number."; errEl.hidden = false; } return; }
          var email = (emailEl && emailEl.value || "").trim();
          submit.disabled = true; var prev = submit.textContent; submit.textContent = "Sending…";
          fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "sms_subscribe", phone: raw, email: email }) })
            .then(function (r) { return r.json().catch(function () { return null; }); })
            .then(function (j) {
              if (j && j.ok) {
                submit.textContent = "Success!";
                setSmsRow(true);
                setTimeout(function () {
                  m.classList.remove("show");
                  submit.disabled = false; submit.textContent = prev;
                  if (phoneEl) phoneEl.value = ""; if (emailEl) emailEl.value = "";
                }, 2000);
              } else {
                if (errEl) { errEl.textContent = "Couldn't start the signup — please try again."; errEl.hidden = false; }
                submit.disabled = false; submit.textContent = prev;
              }
            })
            .catch(function () { if (errEl) { errEl.textContent = "Couldn't start the signup — please try again."; errEl.hidden = false; } submit.disabled = false; submit.textContent = prev; });
        });
      }
      function setupCarousel() {
        var el = region("redeem"); if (!el) return;
        var wrap = el.closest(".rw-tiles-wrap"); if (!wrap) return;
        var prev = wrap.querySelector('[data-rw-caro="prev"]'), next = wrap.querySelector('[data-rw-caro="next"]');
        function step() { return Math.max(160, Math.round(el.clientWidth * 0.8)); }
        function updateArrows() {
          if (!prev || !next) return;
          var atStart = el.scrollLeft <= 2;
          var atEnd = el.scrollLeft >= (el.scrollWidth - el.clientWidth - 2);
          prev.hidden = atStart;
          next.hidden = atEnd;
        }
        if (!wrap.__caroWired) {
          wrap.__caroWired = 1;
          if (prev) prev.addEventListener("click", function () { el.scrollBy({ left: -step(), behavior: "smooth" }); });
          if (next) next.addEventListener("click", function () { el.scrollBy({ left: step(), behavior: "smooth" }); });
          el.addEventListener("scroll", function () { updateArrows(); }, { passive: true });
          /* click-and-drag (mouse): threshold >6px = drag; suppress the click that follows a real drag */
          var down = false, moved = false, startX = 0, startScroll = 0;
          el.addEventListener("pointerdown", function (e) {
            if (e.pointerType === "touch") return;   /* touch already scrolls natively */
            down = true; moved = false; startX = e.clientX; startScroll = el.scrollLeft;
          });
          el.addEventListener("pointermove", function (e) {
            if (!down) return;
            var dx = e.clientX - startX;
            if (!moved && Math.abs(dx) > 6) { moved = true; el.classList.add("rw-dragging"); }
            if (moved) { el.scrollLeft = startScroll - dx; e.preventDefault(); }
          });
          function endDrag() {
            if (!down) return; down = false;
            if (moved) { el.classList.remove("rw-dragging"); el.__suppressClick = 1; setTimeout(function () { el.__suppressClick = 0; }, 0); }
          }
          el.addEventListener("pointerup", endDrag);
          el.addEventListener("pointerleave", endDrag);
          el.addEventListener("pointercancel", endDrag);
          el.addEventListener("click", function (e) { if (el.__suppressClick) { e.stopPropagation(); e.preventDefault(); } }, true);   /* capture: kill the click that ends a drag */
        }
        updateArrows();
      }
    function renderTiles(options, source) {
      var el = region("redeem"); if (!el) return;
      var rewards = toArray(options);
      if (!rewards.length) { if (source !== "loyalty") hide(el); return; }
      show(el);
      var balance = state.balance;
      var closest = -1, closestGap = Infinity;
      rewards.forEach(function (o, i) { var c = redeemCost(o); if (c != null && balance != null && balance < c) { var g = c - balance; if (g < closestGap) { closestGap = g; closest = i; } } });
      el.innerHTML = rewards.slice(0, 12).map(function (o, i) {
        var cost = costOf(o), name = nameOf(o), ec = redeemCost(o);
        var ruleId = pick(o, ["id", "rewardId", "pointRedeemRuleId", "_id"], null);
        var mapUrl = mapLookup(ruleId, name);            // map is PRIMARY (by id, then name)
        var im = mapUrl ? null : imageFromReward(o);     // fallback only if unmapped
        var directUrl = mapUrl || (im && im.url) || null;
        var handle = (!directUrl && im && im.handle) ? im.handle : null;
        var eligible = (balance != null && ec != null) ? (balance >= ec) : null;
        var isClosest = (i === closest);
        var costStr = cost != null ? (cost.toLocaleString() + " pts") : "";
        var capChrome = '<span class="pc">' + esc(costStr) + '</span>' + (eligible === false ? '<div class="locko"><span><svg class="locki" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>' + esc((ec - balance).toLocaleString()) + ' to go</span></div>' : '');
        var cap = directUrl
          ? '<div class="cap cap-img"><img src="' + esc(directUrl) + '" alt="' + esc(name) + '" loading="lazy">' + capChrome + '</div>'
          : '<div class="cap v' + (i % 4) + '"><span class="ph">' + GLYPHS[i % GLYPHS.length] + '</span>' + capChrome + '</div>';
        var status = eligible === true ? "Eligible" : (eligible === false ? ((ec - balance).toLocaleString() + " pts to go") : "");
        var track = (isClosest && cost) ? '<div class="ptrack"><i style="width:' + Math.max(0, Math.min(100, Math.round(balance / cost * 100))) + '%"></i></div>' : "";
        var btn = (eligible === false)
          ? '<div class="rw-act" data-rw-act><div class="btn btn-block rw-locked-cta" aria-disabled="true">' + esc(RW_INSUFFICIENT) + "</div></div>"
          : '<div class="rw-act" data-rw-act></div>';
        var cls = "ptile" + (eligible === false ? " locked" : "");
        return '<div class="' + cls + '"' + (handle ? ' data-rw-handle="' + esc(handle) + '"' : "") + ">" + cap + '<div class="pn">' + esc(name) + '</div><div class="pspace"></div>' + btn + "</div>";
      }).join("");
      resolveTileImages();
      /* wire each eligible tile to its EXACT option object (closure over o), by index = render order */
      var view = rewards.slice(0, 12);
      Array.prototype.forEach.call(el.querySelectorAll(".ptile"), function (tile, i) {
        wireTilt(tile);
        var o = view[i]; if (!o) return;
        var ec = redeemCost(o);
        var eligible = (balance != null && ec != null) ? (balance >= ec) : null;
        if (eligible !== false) mountActions(tile, o);
        else {
          tile.style.cursor = "pointer";
          tile.setAttribute("role", "button");
          tile.setAttribute("tabindex", "0");
          tile.addEventListener("pointerdown", function () {
            if (TILT_REDUCE) return;
            tile.classList.remove("rw-shake"); void tile.offsetWidth; tile.classList.add("rw-shake");
            setTimeout(function () { tile.classList.remove("rw-shake"); }, 420);
          });
          tile.addEventListener("click", function () { setTimeout(function () { openNotEnough(o); }, TILT_REDUCE ? 0 : 160); });
          tile.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openNotEnough(o); } });
        }
      });
      renderHeroBar(closest >= 0 ? rewards[closest] : null);
      setupCarousel();
    }

    /* ---- redeemed-&-ready shelf (UNUSED rewards from customer-loyalty) ---- */
    function renderShelf(rewards) {
      state.lastRewards = rewards;   /* remember so render() can repaint titles in place once the bridge map loads */
      var sh = region("shelf"); if (!sh) return;
      var wrap = region("shelf-wrap") || sh; // hides the heading together with the shelf
      var cta = sh.getAttribute("data-shelf-cta") || "Add to order"; state.shelfCta = cta;
      var unused = toArray(rewards).filter(function (r) {
        var st = String(pick(r, ["status"], "")).toUpperCase();
        return st === "UNUSED" || (st === "" && pick(r, ["usedAt"], "__") == null);
      });
      if (unused.length) {
        var note = sh.getAttribute("data-shelf-note") || "";   /* optional copy from settings (section passes data-shelf-note) */
        var noteHtml = note ? ('<div class="shelf-note">' + esc(note) + '</div>') : "";
        sh.innerHTML = noteHtml + unused.slice(0, 6).map(function (r) {
          var nm = pick(r, ["description", "name", "rewardName"], "Reward");
          var code = pick(r, ["discountCode", "code", "couponCode"], null);
          var exp = fmtDate(pick(r, ["expireDate", "expireAt", "expiresAt"], null));
          var ruleId = pick(r, ["pointRedeemRuleId", "redeemRuleId", "pointRedeemRuleRuleId"], null);
          var title = (state.ruleNameById && state.ruleNameById[String(ruleId)]) || nm;   /* clean catalog rule name; raw reward.description is the fallback */
          var vid = pick(r, ["variantId", "variant_id"], null);
          var mapUrl = mapLookup(ruleId, nm);             // merchant reward_image block (curated, reward-level)
          var im = mapUrl ? null : imageFromReward(r);
          var handle = (im && im.handle) || pick(r, ["productHandle", "handle"], null) || (productInfo(r) && pick(productInfo(r), ["handle", "productHandle"], null));
          /* same fallback chain as the picker, reusing its warm caches: picked-variant image → product featured → reward image → glyph */
          var warmVar = (handle && vid && imgByHandle[handle]) ? imgByHandle[handle][String(vid)] : null;
          var warmProd = (handle && prodImgByHandle[handle]) ? prodImgByHandle[handle] : null;
          var url = warmVar || warmProd || mapUrl || (im && im.url) || null;
          var thumb = url ? ('<img src="' + esc(url) + '" alt="' + esc(nm) + '">') : "🎁";
          var meta = (code ? ("Code " + code) : "Ready") + (exp ? (" · expires " + exp) : "");
          /* variant label (e.g. "Dark / Whole Bean") from the same products.js cache, REPLACING the meta line;
             cold cache shows meta now and upgrades on lazy load (data-rw-variant + data-rw-meta, via resolveTileImages). */
          var warmTitle = (handle && vid && varTitleByHandle[handle]) ? varTitleByHandle[handle][String(vid)] : null;
            var varLabel = warmTitle || "";
            var codeLine = meta;
          var titleCold = !!vid && !warmTitle;
          var lazyHandle = (handle && (!url || titleCold)) ? handle : null;   // lazy upgrade for a cold image and/or a cold variant label
          return '<div class="shelf"' + (lazyHandle ? ' data-rw-handle="' + esc(lazyHandle) + '"' : "") + (titleCold ? ' data-rw-variant="' + esc(String(vid)) + '" data-rw-meta="1"' : "") + '>' +
            '<div class="si">' + thumb + '</div>' +
            '<div class="sinfo"><span class="sbadge">FREE</span><div class="st">' + esc(title) + '</div><div class="sd">' + esc(varLabel) + '</div><div class="scode">' + esc(codeLine) + '</div>' +
            '<div class="rw-shelf-act" data-rw-shelf-act><button type="button" class="btn btn-primary btn-sm rw-shelf-add" data-rule-id="' + esc(String(ruleId == null ? "" : ruleId)) + '" data-code="' + esc(code || "") + '">' + esc(cta) + '</button></div></div></div>';
        }).join("");
        var view = unused.slice(0, 6);   /* wire each Add button to its reward (closure over r), by render order */
        Array.prototype.forEach.call(sh.querySelectorAll(".rw-shelf-add"), function (btn, i) {
          var rr = view[i]; if (!rr) return;
          btn.addEventListener("click", function () { shelfAddToOrder(rr, btn); });
        });
        show(sh); show(wrap); resolveTileImages();
      } else if (sh.children.length) { show(sh); show(wrap); } // manual blocks / design sample
      else { hide(wrap); }
    }
    var MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    function fmtDate(v) { if (!v) return null; var d = new Date(v); return isNaN(d.getTime()) ? null : (MON[d.getMonth()] + " " + d.getDate() + ", " + d.getFullYear()); }

    /* ---- Stage 2: shelf "Add to order" — add the reward's variant as a one-time line + apply its code ---- */
    function activeContractId() {                                                   /* mirrors moContractId (members-only) */
      var c = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
      var d = (c && c.dto) || c || {};
      return String(d.subscriptionContractId || d.contractId || d.id || "");
    }
    function shelfMakeAddBtn(r) {                                                    /* a fresh, wired Add button (restore after cancel/error) */
      var b = document.createElement("button");
      b.type = "button"; b.className = "btn btn-primary btn-sm rw-shelf-add"; b.textContent = state.shelfCta || "Add to order";
      b.addEventListener("click", function () { shelfAddToOrder(r, b); });
      return b;
    }
    function shelfErr(slot, r, text) {                                              /* rust inline error + restore the Add button so the user can retry */
      slot.innerHTML = "";
      var e = document.createElement("div"); e.style.cssText = "font:600 11px inherit;line-height:1.3;color:#BC5530;margin-bottom:5px"; e.textContent = text;
      slot.appendChild(e); slot.appendChild(shelfMakeAddBtn(r));
    }
    function shelfDoAdd(r, slot, cid, code, addedVariantId, recurring) {
      slot.innerHTML = "";
      var working = document.createElement("button"); working.type = "button"; working.className = "btn btn-primary btn-sm"; working.disabled = true; working.textContent = "Adding…"; slot.appendChild(working);
      function bgRefresh() {   /* un-awaited background refresh (items list + loyalty ledger) */
        try { window.LRCMP_REFRESH_AFTER_CHANGE && window.LRCMP_REFRESH_AFTER_CHANGE(); } catch (e) {}
        try { refreshLoyaltyLive(); } catch (e) {}
      }
      function shelfSuccess() {
        /* 1. button → Added ✓ immediately (before any refresh) */
        slot.innerHTML = "";
        var done = document.createElement("button"); done.type = "button"; done.className = "btn btn-primary btn-sm"; done.disabled = true; done.textContent = "Added ✓"; slot.appendChild(done);
        /* 2. scroll the member to the items list */
        var el = document.querySelector('[data-mp-region="drop-items"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        /* 3. fire the refresh in the BACKGROUND — do NOT await */
        bgRefresh();
      }
      function applyOnce() {   /* never rejects: parse failure / network → null (treated as a failure) */
        return fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "apply_discount_code", contractId: cid, code: code }) })
          .then(function (r2) { return r2.json().catch(function () { return null; }); })
          .catch(function () { return null; });
      }
      function rollback(lastJ) {
        /* Genuine apply failure → remove the just-added one-time line so the member isn't left with a paid line.
           Each redemption has its OWN unique code, so there's no duplicate/already-applied special-casing here. */
        fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "get_contract_lines", contractId: cid }) })
          .then(function (r3) { return r3.json().catch(function () { return null; }); })
          .then(function (jl) {
            var lines = (jl && jl.ok && Array.isArray(jl.lines)) ? jl.lines : [];
            /* OUR line = one-time (basePrice == null) + the variant we added. sellingPlanId is on every line → not a signal. */
            var candidates = lines.filter(function (ln) { return ln && ln.basePrice == null && String(ln.variantId) === String(addedVariantId); });
            if (candidates.length === 1) {
              var cand = candidates[0];
              if (cand.hasDiscount === true) { shelfSuccess(); return; }   /* code DID apply despite the error — keep the line */
              var rmUrl = "/apps/subscriptions/cp/api/subscription-contracts-remove-line-item?contractId=" + encodeURIComponent(cid) +
                "&lineId=" + encodeURIComponent(cand.id) + "&removeDiscount=false";
              fetch(rmUrl, { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" } })
                .then(function (rr) {
                  var removed = !(!rr.ok || rr.type === "opaqueredirect" || rr.status === 0);
                  shelfErr(slot, r, removed
                    ? "Couldn't apply the reward, so we removed the item — please try again."
                    : "Couldn't apply the reward and couldn't auto-remove it — please review your subscription or contact support.");
                  if (window.LRCMP_REFRESH_AFTER_CHANGE) window.LRCMP_REFRESH_AFTER_CHANGE();   /* items-only: keep the in-place error + retry */
                })
                .catch(function () {
                  shelfErr(slot, r, "Couldn't apply the reward and couldn't auto-remove it — please review your subscription or contact support.");
                  if (window.LRCMP_REFRESH_AFTER_CHANGE) window.LRCMP_REFRESH_AFTER_CHANGE();   /* items-only: keep the in-place error + retry */
                });
            } else {   /* 0 or >1 candidates → ambiguous, NEVER remove */
              shelfErr(slot, r, "Couldn't apply the reward — the item may still be on your order. Please review your subscription or contact support.");
              if (window.LRCMP_REFRESH_AFTER_CHANGE) window.LRCMP_REFRESH_AFTER_CHANGE();   /* items-only: keep the in-place error + retry */
            }
          })
          .catch(function () {
            shelfErr(slot, r, "Couldn't apply the reward — the item may still be on your order. Please review your subscription or contact support.");
            if (window.LRCMP_REFRESH_AFTER_CHANGE) window.LRCMP_REFRESH_AFTER_CHANGE();   /* items-only: keep the in-place error + retry */
          });
      }
      function doAddThenApply() {   /* NO-OVERLAP path: add a one-time line, apply, retry, rollback on genuine failure (unchanged) */
        var addUrl = "/apps/subscriptions/cp/api/v2/subscription-contracts-add-line-item?contractId=" + encodeURIComponent(cid) +
          "&variantId=" + encodeURIComponent(addedVariantId) + "&quantity=1&isOneTimeProduct=" + (recurring ? "false" : "true");
        fetch(addUrl, { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" } })
          .then(function (resp) {
            if (!resp.ok || resp.type === "opaqueredirect" || resp.status === 0) throw new Error("add_failed");   /* one-time line is in */
            return applyOnce().then(function (j) {
              if (j && j.ok === true) { shelfSuccess(); return; }                                  /* attempt 1 OK */
              return new Promise(function (res) { setTimeout(res, 900); }).then(function () {        /* wait ~900ms, retry ONCE */
                return applyOnce().then(function (j2) {
                  if (j2 && j2.ok === true) { shelfSuccess(); return; }                              /* retry OK */
                  rollback(j2 || j);                                                                 /* still failing → rollback */
                });
              });
            });
          })
          .catch(function () {
            shelfErr(slot, r, "Couldn't add the item.");   /* only reached on add_failed; apply path no longer throws */
          });
      }
      function applyToExisting() {   /* OVERLAP path: member already subscribes to this variant → apply the code to
                                        the existing paid line so it goes free for the cycle. NO add-line, NO rollback
                                        (nothing was added to remove). recurringCycleLimit is 1, so only this cycle. */
        applyOnce().then(function (j) {
          if (j && j.ok === true) { shelfSuccess(); return; }                                      /* attempt 1 OK */
          return new Promise(function (res) { setTimeout(res, 900); }).then(function () {            /* wait ~900ms, retry ONCE */
            return applyOnce().then(function (j2) {
              if (j2 && j2.ok === true) { shelfSuccess(); return; }                                  /* retry OK */
              shelfErr(slot, r, "Couldn't apply the reward — please try again or contact support."); /* nothing added → nothing to roll back */
              if (window.LRCMP_REFRESH_AFTER_CHANGE) window.LRCMP_REFRESH_AFTER_CHANGE();
            });
          });
        });
      }
      /* OVERLAP branch: does the contract already carry a PAID recurring line (basePrice != null) of this variant?
         If so, applying a variant-scoped code would free that paid line too — so apply to the existing line instead
         of adding a second one. Otherwise (new product) keep the proven add+apply+rollback path untouched. */
      var ovlVid = pick(r, ["variantId", "variant_id"], null) || addedVariantId;
      fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get_contract_lines", contractId: cid }) })
        .then(function (r3) { return r3.json().catch(function () { return null; }); })
        .then(function (jl) {
          var lines = (jl && jl.ok && Array.isArray(jl.lines)) ? jl.lines : [];
          var existing = lines.filter(function (ln) { return ln && ln.basePrice != null && String(ln.variantId) === String(ovlVid); });
          var overlap = existing.length > 0;
          if (overlap) applyToExisting(); else doAddThenApply();
        })
        .catch(function () {
          doAddThenApply();   /* can't determine overlap → default to the proven add path */
        });
    }
    function shelfAddToOrder(r, btn) {
      var slot = btn.closest("[data-rw-shelf-act]") || btn.parentNode;
      var code = pick(r, ["discountCode", "code", "couponCode"], null);
      var ruleId = String(pick(r, ["pointRedeemRuleId", "redeemRuleId", "pointRedeemRuleRuleId"], ""));
      if (!code) { shelfErr(slot, r, "This reward has no code to apply."); return; }
      var cid = activeContractId();
      if (!cid) { shelfErr(slot, r, "No active subscription."); return; }
      if (window.LRCMP_IS_ACTIVE_PREPAID && window.LRCMP_IS_ACTIVE_PREPAID()) {     /* prepaid: items ship every delivery → point to checkout */
        slot.innerHTML = "";
        var cal = document.createElement("div");
        cal.style.cssText = "font:600 11px inherit;line-height:1.4;color:#223F4E;max-width:210px";
        cal.innerHTML = "Prepaid plans can't add items here — use code <b>" + esc(code) + "</b> at checkout <a href=\"/collections/all\" style=\"color:#BC5530;font-weight:700;text-decoration:none\">&rarr;</a>";
        slot.appendChild(cal);
        return;
      }
      btn.disabled = true; btn.textContent = "Working…";
      /* The flavor is now picked at REDEEM, so the code is scoped to r.variantId. Add EXACTLY that variant and
         SKIP the add-time picker — the added line must not diverge from the code's variant (else apply fails). */
      var mintedVid = pick(r, ["variantId", "variant_id"], null);
      if (mintedVid) { shelfDoAdd(r, slot, cid, code, mintedVid); return; }
      /* Legacy fallback only (reward carries no variant): resolve + let the member pick at add. */
      resolveRedeemVariants(r).then(function (choices) {
        if (choices && choices.length) return choices;
        return resolveRedeemVariants(state.ruleRowById[ruleId] || {});
      }).then(function (choices) {
        if (choices && choices.length > 1) {
          showPicker(choices, slot,
            function (c2) { shelfDoAdd(r, slot, cid, code, c2.variantId); },
            function () { slot.innerHTML = ""; slot.appendChild(shelfMakeAddBtn(r)); });
          return;
        }
        if (choices && choices.length === 1) { shelfDoAdd(r, slot, cid, code, choices[0].variantId); return; }
        shelfErr(slot, r, "Couldn't resolve this reward's flavors — please try again or contact support.");
      });
    }

    /* ---- review row → Done from the identity hasReviewed signal (event + boot getter) ---- */
    function markReviewRowDone() {
      var rows = root.querySelectorAll(".erow");
      Array.prototype.forEach.call(rows, function (row) {
        if (row.classList.contains("done")) return;
        var note = (row.getAttribute("data-earn-note") || "").toLowerCase();
        if (note.indexOf("review") < 0) return;
        row.classList.add("done");
        var ep = row.querySelector(".ep"); if (ep) ep.innerHTML = '<span class="ep-done"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>Done</span>';
      });
    }

    /* ---- ways-to-earn ✓ from rewardedFor* booleans ---- */
    function markEarnedFromFlags(cust) {
      if (!cust) return;
      Array.prototype.forEach.call(root.querySelectorAll(".erow[data-flag-key]"), function (row) {
        if (row.classList.contains("done")) return;
        var key = (row.getAttribute("data-flag-key") || "").trim();
        if (key === "sms") return;   /* SMS Done is driven by live Klaviyo consent, not the Appstle sticky flag — resolved async below */
        var field = FLAG_FIELD[key];
        if (!field || !pick(cust, [field, "data." + field, "customer." + field], false)) return;
        row.classList.add("done");
        var ep = row.querySelector(".ep"); if (ep) ep.innerHTML = '<span class="ep-done"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>Done</span>';
        var a = row.querySelector("a.en"); if (a) { var dv = document.createElement("div"); dv.className = "en"; dv.textContent = a.textContent; a.parentNode.replaceChild(dv, a); }
      });
      (function smsLiveStatus() {
        var row = root.querySelector('.erow[data-flag-key="sms"]'); if (!row) return;
        fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "sms_status" }) })
          .then(function (r) { return r.json().catch(function () { return null; }); })
          .then(function (j) {
            if (!j || !j.ok) return;   /* read failed → leave whatever state is (fail-safe) */
            setSmsRow(j.subscribed === true);   /* authoritative: subscribed→green, not→claimable */
          })
          .catch(function () {});   /* fail-open: leave current state */
      })();
      function recomputeEarnSum() {
        var sum = 0;
        Array.prototype.forEach.call(root.querySelectorAll(".erow[data-flag-key]"), function (row) {
          if (row.classList.contains("done")) return;
          var p = parseInt(row.getAttribute("data-earn-points") || "0", 10);
          if (!isNaN(p)) sum += p;
        });
        var pill = root.querySelector("[data-earn-sum]");
        if (pill) { if (sum > 0) { pill.textContent = sum.toLocaleString() + " pts to claim"; pill.hidden = false; } else { pill.hidden = true; } }
      }
      recomputeEarnSum();
      (function wireEarnActions() {
        var rows = root.querySelectorAll(".erow");
        setupSmsModal();
        Array.prototype.forEach.call(rows, function (row) {
          if (row.__earnWired) return; row.__earnWired = 1;
          var key = (row.getAttribute("data-flag-key") || "").trim();
          var note = (row.getAttribute("data-earn-note") || "").toLowerCase();
          var earnUrl = (row.getAttribute("data-earn-url") || "").trim();
          if (earnUrl) {
            // whole-row tappable → open the configured URL in a new tab (desktop + mobile).
            // cursor:pointer is CSS-driven (.erow[data-earn-url]:not(.done)) so it auto-clears once the row is earned.
            // neutralize the native label anchor so it doesn't double-open (row handler is source of truth)
            var labelA = row.querySelector("a.en");
            if (labelA) { labelA.addEventListener("click", function (ev) { ev.preventDefault(); ev.stopPropagation(); }); }
            row.addEventListener("click", function () {
              if (row.classList.contains("done")) return;   // earned rows are complete — not tappable
              window.open(earnUrl, "_blank", "noopener");
            });
            return;   // url row fully handled; skip SMS/review branches
          }
          // SMS row → open the SMS modal
          if (key === "sms" || note.indexOf("text") >= 0 || note.indexOf("sms") >= 0) {
            row.style.cursor = "pointer";
            row.addEventListener("click", function () {
              if (row.classList.contains("done")) return;
              var scope = root.closest(".rw-wrap") || document;
              var m = scope.querySelector('[data-modal="smsModal"]') || document.querySelector('[data-modal="smsModal"]');
              if (m) m.classList.add("show");
            });
            return;
          }
          // Review row → open the existing identity review modal (must pass an element inside .idn)
          if (note.indexOf("review") >= 0) {
            row.style.cursor = "pointer";
            row.addEventListener("click", function () {
              if (row.classList.contains("done")) return;
              if (!window.LRCMPI || !window.LRCMPI.openReviewForm) return;
              var idn = document.querySelector(".idn"); if (!idn) return;
              window.LRCMPI.openReviewForm(idn);   /* idn itself is inside .idn → closest('.idn') resolves */
            });
            return;
          }
        });
      })();
      (function armReveal() {
        var rw = root.classList ? root : null; if (!rw) return;
        var earn = root.querySelector(".earn"); if (!earn) return;
        if (TILT_REDUCE) { root.classList.add("reveal-armed"); Array.prototype.forEach.call(root.querySelectorAll(".reveal"), function (n) { n.classList.add("in"); }); return; }
        root.classList.add("reveal-armed");
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); } });
        }, { threshold: 0.15 });
        Array.prototype.forEach.call(root.querySelectorAll(".reveal"), function (n) { io.observe(n); });
      })();
    }

    /* ---- consume the published (bridge) payload: balance + fallback catalog ---- */
    function render(payload) {
      if (!payload) return;
      var cust = payload.customer, redeem = toArray(payload.redeem);
      var custOk = cust && typeof cust === "object";
      if (custOk) state.customer = cust;
      if (custOk || redeem.length || DESIGN) { root.hidden = false; hide(region("empty")); }
      var b = num(pointsOf(cust)); if (b != null) state.balance = b;
      renderPoints(state.balance);
      if (!state.catalogFromLoyalty) renderTiles(redeem, "bridge");
      /* clean rule names for the redeemed shelf, sourced from the BRIDGE redeem rows (the loyalty
         catalog endpoints 404). o.id = rule id; nameOf(o) = the clean label the tiles already show. */
      state.ruleNameById = state.ruleNameById || {};
      state.ruleRowById = state.ruleRowById || {};   /* full bridge row by rule id — carries productData for shelf variant resolution */
      redeem.forEach(function (o) {
        var rid = pick(o, ["id", "rewardId", "pointRedeemRuleId", "_id"], null);
        var rnm = nameOf(o);
        if (rid != null && rnm) state.ruleNameById[String(rid)] = rnm;
        if (rid != null) state.ruleRowById[String(rid)] = o;
      });
      /* race: the shelf may have painted before the bridge arrived — repaint in place (no re-fetch) */
      if (state.lastRewards) renderShelf(state.lastRewards);
    }

    /* ---- loyalty proxy: customer-loyalty + variant-aware catalog ---- */
    function loadLoyalty() {
      clFetch(LOYALTY_BASE).then(function (cust) {
        if (!cust || typeof cust !== "object") return;
        state.customer = cust;
        root.hidden = false;
        var b = num(pointsOf(cust)); if (b == null) b = num(pick(cust, ["availablePoints"], null));
        if (b != null) { state.balance = b; renderPoints(b); }
        renderShelf(toArray(pick(cust, ["rewards"], [])));
        markEarnedFromFlags(cust);
      });
      loadCatalog();
      try { ensureRedeemRules(); } catch (e) {}   /* warm the redeem-rule productData cache for the picker */
    }
    function loadCatalog() {
      var cands = [CATALOG_PATH, "point-redeem-rule-by-shop", "point-redeem-rules-by-shop", "point-redeem-rules", "point-redeem-rule"].filter(function (v, i, a) { return v && a.indexOf(v) === i; });
      (function tryNext(i) {
        if (i >= cands.length) return;
        lapi(cands[i]).then(function (res) {
          var rows = toArray(res);
          var looksLikeCatalog = rows.length && rows.some(function (r) { return costOf(r) != null && nameOf(r, "") !== ""; });
          if (looksLikeCatalog) { state.catalogFromLoyalty = true; renderTiles(rows, "loyalty"); }
          else tryNext(i + 1);
        });
      })(0);
    }

    /* ---- points-info modal (perk pill → explainer; scrim/sheet pattern) ---- */
    function pointsModal() { return (root.closest(".rw-wrap") || root).querySelector('[data-modal="pointsModal"]'); }
    function openPointsModal() { var m = pointsModal(); if (m) m.classList.add("show"); }
    function closePointsModal() { var m = pointsModal(); if (m) m.classList.remove("show"); }
    function closeOpenModals() { Array.prototype.forEach.call((root.closest(".rw-wrap") || root).querySelectorAll(".scrim.show"), function (m) { m.classList.remove("show"); }); }
    var perkBtn = root.querySelector("[data-mp-perk]");
    if (perkBtn) perkBtn.addEventListener("click", function (e) { e.preventDefault(); openPointsModal(); });
    (root.closest(".rw-wrap") || root).addEventListener("click", function (e) {
      if (e.target.closest("[data-modal-close]")) { e.preventDefault(); var mc = e.target.closest(".scrim"); if (mc) mc.classList.remove("show"); return; }   // X closes its OWN scrim (points modal OR redeem sheet)
      var sc = e.target.closest(".scrim"); if (sc && e.target === sc) sc.classList.remove("show"); // backdrop click closes that scrim
    });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") closeOpenModals(); });

    /* boot */
    if (DESIGN) {
      render(sampleLoyalty());
      renderShelf(sampleCustomer().rewards);
      markEarnedFromFlags(sampleCustomer());
      return;
    }
    if (window.LRCMP_LOYALTY) render(window.LRCMP_LOYALTY);
    document.addEventListener("lrc-mp:loyalty", function (e) { render(e.detail); });
    document.addEventListener("lrc:review-state", function (e) { if (e && e.detail && e.detail.hasReviewed) markReviewRowDone(); });
    loadLoyalty();
    // boot getter fallback: identity may have resolved hasReviewed before this listener registered
    try { if (window.LRCMPI && typeof window.LRCMPI.hasReviewed === "function" && window.LRCMPI.hasReviewed()) markReviewRowDone(); } catch (e) {}
  });
})();
